import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh,
  CloudDownload,
  Refresh,
  Restore,
  SaveAlt,
  Storage,
} from '@mui/icons-material';
import {
  databaseService,
  DatabaseBackupItem,
  DatabaseStatsResponse,
  DatabaseTableStat,
} from '../services/api';

const statusColor = (status: DatabaseTableStat['status']): 'success' | 'warning' | 'error' => {
  if (status === 'healthy') return 'success';
  if (status === 'warning') return 'warning';
  return 'error';
};

const DatabaseManagementPage: React.FC = () => {
  const [stats, setStats] = useState<DatabaseStatsResponse | null>(null);
  const [backups, setBackups] = useState<DatabaseBackupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [backupLabel, setBackupLabel] = useState('');
  const [retentionDays, setRetentionDays] = useState(14);
  const [restoreTarget, setRestoreTarget] = useState<DatabaseBackupItem | null>(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const showNotification = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const loadData = useCallback(async (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    }

    try {
      const [statsData, backupsData] = await Promise.all([
        databaseService.getStats(),
        databaseService.listBackups(),
      ]);
      setStats(statsData);
      setBackups(backupsData);
    } catch (error: any) {
      console.error(error);
      showNotification(error?.response?.data?.error?.message || 'Errore caricamento dati database', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadData(true);
  }, [loadData]);

  const runAction = async (actionName: string, action: () => Promise<void>) => {
    try {
      setBusyAction(actionName);
      await action();
      await loadData(false);
    } catch (error: any) {
      console.error(error);
      showNotification(error?.response?.data?.error?.message || 'Operazione non riuscita', 'error');
    } finally {
      setBusyAction(null);
    }
  };

  const handleCreateBackup = async () => {
    await runAction('backup', async () => {
      const backup = await databaseService.createBackup(backupLabel);
      setBackupLabel('');
      showNotification(`Backup creato: ${backup.fileName}`);
    });
  };

  const handleDownloadBackup = async (backup: DatabaseBackupItem) => {
    await runAction(`download:${backup.fileName}`, async () => {
      const response = await databaseService.downloadBackup(backup.fileName);
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = backup.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showNotification(`Download avviato: ${backup.fileName}`);
    });
  };

  const handleRestoreBackup = async () => {
    if (!restoreTarget) return;

    await runAction('restore', async () => {
      await databaseService.restoreBackup(restoreTarget.fileName);
      showNotification(`Ripristino completato da ${restoreTarget.fileName}`);
      setRestoreTarget(null);
    });
  };

  const handleCleanupBackups = async () => {
    await runAction('cleanup', async () => {
      const result = await databaseService.cleanupBackups(retentionDays);
      showNotification(`Pulizia completata: ${result.removedCount} backup rimossi`);
    });
  };

  const handleOptimize = async () => {
    await runAction('optimize', async () => {
      await databaseService.optimize();
      showNotification('Ottimizzazione database completata');
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  const database = stats?.database;
  const tables = stats?.tables || [];
  const latestBackup = stats?.backupSummary.latestBackup;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1">
          Gestione Database
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => loadData(false)}
          disabled={!!busyAction}
        >
          Aggiorna
        </Button>
      </Box>

      {database?.maintenanceInProgress && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Operazione in corso: {database.maintenanceInProgress}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Database</Typography>
          <Typography variant="h6">{database?.name || '-'}</Typography>
          <Typography variant="body2" color="text.secondary">Dimensione: {database?.size || '-'}</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Tabelle</Typography>
          <Typography variant="h6">{database?.totalTables ?? 0}</Typography>
          <Typography variant="body2" color="text.secondary">Record stimati: {(database?.totalRecords ?? 0).toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Backup salvati</Typography>
          <Typography variant="h6">{stats?.backupSummary.count ?? 0}</Typography>
          <Typography variant="body2" color="text.secondary">
            Ultimo: {latestBackup ? new Date(latestBackup.createdAt).toLocaleString() : 'Nessuno'}
          </Typography>
        </Paper>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Operazioni</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Etichetta backup (opzionale)"
            size="small"
            value={backupLabel}
            onChange={(event) => setBackupLabel(event.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<SaveAlt />}
            onClick={handleCreateBackup}
            disabled={!!busyAction}
          >
            Crea Backup
          </Button>
          <TextField
            label="Retenzione (giorni)"
            type="number"
            size="small"
            value={retentionDays}
            inputProps={{ min: 1, max: 3650 }}
            onChange={(event) => {
              const parsed = Math.max(1, Math.min(3650, Number(event.target.value) || 14));
              setRetentionDays(parsed);
            }}
          />
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CloudDownload />}
            onClick={handleCleanupBackups}
            disabled={!!busyAction}
          >
            Pulisci Backup Vecchi
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<AutoFixHigh />}
            onClick={handleOptimize}
            disabled={!!busyAction}
          >
            Ottimizza DB
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tabella</TableCell>
                <TableCell>Record (stima)</TableCell>
                <TableCell>Dimensione</TableCell>
                <TableCell>Stato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tables.map((table) => (
                <TableRow key={table.name}>
                  <TableCell>{table.name}</TableCell>
                  <TableCell>{table.count.toLocaleString()}</TableCell>
                  <TableCell>{table.size}</TableCell>
                  <TableCell>
                    <Chip label={table.status} color={statusColor(table.status)} size="small" />
                  </TableCell>
                </TableRow>
              ))}
              {tables.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Nessuna tabella trovata
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Backup</TableCell>
                <TableCell>Dimensione</TableCell>
                <TableCell>Creato il</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.map((backup) => (
                <TableRow key={backup.fileName}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{backup.fileName}</TableCell>
                  <TableCell>{backup.size}</TableCell>
                  <TableCell>{new Date(backup.createdAt).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<CloudDownload />}
                      onClick={() => handleDownloadBackup(backup)}
                      disabled={!!busyAction}
                    >
                      Download
                    </Button>
                    <Button
                      size="small"
                      color="warning"
                      startIcon={<Restore />}
                      onClick={() => setRestoreTarget(backup)}
                      disabled={!!busyAction}
                    >
                      Ripristina
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {backups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Nessun backup disponibile
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!restoreTarget} onClose={() => setRestoreTarget(null)}>
        <DialogTitle>Conferma ripristino</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Stai per ripristinare il database dal backup:
          </Typography>
          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', mt: 1 }}>
            {restoreTarget?.fileName}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Il ripristino sovrascrive i dati correnti. Effettua prima un backup di sicurezza.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)} disabled={!!busyAction}>Annulla</Button>
          <Button onClick={handleRestoreBackup} color="warning" variant="contained" disabled={!!busyAction}>
            Conferma Ripristino
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3500}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
          icon={<Storage fontSize="small" />}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DatabaseManagementPage;
